<template>
  <!-- Desktop: full-screen sliding carousel -->
  <section class="main-mixed-carousel desktop-only">
    <div class="mixed-carousel-track" :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
      <div
        v-for="(slide, index) of normalizedSlides"
        :key="index"
        class="mixed-carousel-slide"
      >
        <img
          :src="slide.image"
          :alt="slide.title"
          class="mixed-carousel-image"
        />

        <div class="mixed-carousel-overlay"></div>
        <div class="mixed-carousel-info">
          <!-- Project breadcrumb -->
          <a v-if="slide.type === 'project'" :href="slide.link" @click.prevent="goToLink(slide)" class="mixed-carousel-breadcrumb">
            <svg width="17" height="10" viewBox="0 0 17 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="7" y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="14" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="14" y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
            </svg>
            <span v-html="slide.title"></span>
          </a>
          <!-- Product breadcrumb -->
          <a v-else-if="slide.type === 'product' && slide.productUrl" :href="slide.productUrl" @click.prevent="goToProduct(slide.productUrl)" class="mixed-carousel-breadcrumb">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            <span v-html="slide.title"></span>
          </a>
          <p v-if="slide.excerpt" class="mixed-carousel-excerpt" v-html="slide.excerpt"></p>
        </div>
      </div>
    </div>

    <!-- Navigation arrows -->
    <button class="mixed-carousel-nav mixed-carousel-prev" @click="userPrev" aria-label="Previous slide">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>
    <button class="mixed-carousel-nav mixed-carousel-next" @click="userNext" aria-label="Next slide">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </button>

    <!-- Dots indicator -->
    <div class="mixed-carousel-dots">
      <button
        v-for="(slide, index) of normalizedSlides"
        :key="index"
        class="mixed-carousel-dot"
        :class="{ active: index === currentIndex }"
        @click="goToSlide(index)"
        :aria-label="`Go to slide ${index + 1}`"
      ></button>
    </div>
  </section>

  <!-- Mobile: horizontal scroll cards -->
  <section class="main-mixed-carousel-mobile mobile-only">
    <div ref="mobileContainer" class="mixed-carousel-mobile-container">
      <div
        v-for="(slide, index) of normalizedSlides"
        :key="index"
        class="mixed-carousel-mobile-card"
        :data-index="index"
      >
        <img
          :src="slide.image"
          :alt="slide.title"
          class="mixed-carousel-mobile-image"
        />

        <div class="mixed-carousel-mobile-overlay"></div>
        <div class="mixed-carousel-mobile-info">
          <a v-if="slide.type === 'project'" :href="slide.link" @click.prevent="goToLink(slide)" class="mixed-carousel-mobile-link">
            <svg width="17" height="10" viewBox="0 0 17 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="7" y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="14" width="3" height="3" fill="white" fill-opacity="0.5"/>
              <rect x="14" y="7" width="3" height="3" fill="white" fill-opacity="0.5"/>
            </svg>
            <span v-html="slide.title"></span>
          </a>
          <a v-else-if="slide.type === 'product' && slide.productUrl" :href="slide.productUrl" @click.prevent="goToProduct(slide.productUrl)" class="mixed-carousel-mobile-link">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            <span v-html="slide.title"></span>
          </a>
          <p v-if="slide.excerpt" class="mixed-carousel-mobile-excerpt" v-html="slide.excerpt"></p>
        </div>
      </div>
    </div>

    <!-- Mobile dots -->
    <div class="mixed-carousel-mobile-dots">
      <button
        v-for="(slide, index) of normalizedSlides"
        :key="index"
        class="mixed-carousel-mobile-dot"
        :class="{ active: index === mobileActiveIndex }"
        :aria-label="`Go to slide ${index + 1}`"
      ></button>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps(['data'])
const currentIndex = ref(0)
const mobileActiveIndex = ref(0)
const mobileContainer = ref(null)
let touchStartX = 0
let touchEndX = 0
let observer = null
let resizeHandler = null
let mobileObserver = null
let autoPlayTimer = null
let autoPlayPauseTimer = null

const getProjectImage = (project) => {
  if (project.featured_image?.full) return project.featured_image.full
  if (project.featured_image?.large) return project.featured_image.large
  if (project.acf?.['acf-single-portfolio-gallery']?.[0]?.url) {
    return project.acf['acf-single-portfolio-gallery'][0].url
  }
  return ''
}

const normalizedSlides = computed(() => {
  if (!props.data.slides) return []
  return props.data.slides.map((slide) => {
    if (slide.slide_type === 'project' && slide.project && slide.project.length > 0) {
      const project = slide.project[0]
      return {
        type: 'project',
        image: getProjectImage(project),
        title: project.post_title,
        excerpt: project.post_excerpt || '',
        link: `/project/${project.post_name}`,
      }
    } else if (slide.slide_type === 'product') {
      return {
        type: 'product',
        image: slide.product_image_url || '',
        title: slide.product_name || 'Product',
        excerpt: '',
        productUrl: slide.product_url || '',
      }
    // Legacy: support existing instagram slides as product slides
    } else if (slide.slide_type === 'instagram') {
      const images = (slide.ig_images || []).map(img => img.url).filter(Boolean)
      return {
        type: 'product',
        image: images[0] || '',
        title: slide.ig_caption || 'Product',
        excerpt: '',
        productUrl: slide.ig_product_url || '',
      }
    }
    return null
  }).filter(Boolean)
})

// Navigation
const toRelativePath = (url) => {
  if (!url) return '/'
  try {
    return new URL(url).pathname
  } catch {
    return url.startsWith('/') ? url : `/${url}`
  }
}

const goToLink = (slide) => {
  if (slide.type === 'project') {
    window.location.href = slide.link
  }
}

const router = useRouter()

const goToProduct = (url) => {
  router.push(toRelativePath(url))
}

const next = () => {
  if (currentIndex.value < normalizedSlides.value.length - 1) {
    currentIndex.value++
  } else {
    currentIndex.value = 0
  }
}

const prev = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  } else {
    currentIndex.value = normalizedSlides.value.length - 1
  }
}

const userNext = () => { pauseAutoPlay(); next() }
const userPrev = () => { pauseAutoPlay(); prev() }
const goToSlide = (index) => {
  pauseAutoPlay()
  currentIndex.value = index
}

// Auto-play
const AUTOPLAY_INTERVAL = 8000
const AUTOPLAY_RESUME_DELAY = 15000

const autoPlayTick = () => {
  if (normalizedSlides.value.length) next()
}

const startAutoPlay = () => {
  stopAutoPlay()
  autoPlayTimer = setInterval(autoPlayTick, AUTOPLAY_INTERVAL)
}

const stopAutoPlay = () => {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer)
    autoPlayTimer = null
  }
}

const pauseAutoPlay = () => {
  stopAutoPlay()
  if (autoPlayPauseTimer) clearTimeout(autoPlayPauseTimer)
  autoPlayPauseTimer = setTimeout(startAutoPlay, AUTOPLAY_RESUME_DELAY)
}

const handleTouchStart = (e) => {
  touchStartX = e.changedTouches[0].screenX
}

const handleTouchEnd = (e) => {
  touchEndX = e.changedTouches[0].screenX
  const diff = touchStartX - touchEndX
  if (Math.abs(diff) > 50) {
    pauseAutoPlay()
    if (diff > 0) next()
    else prev()
  }
}

const isDesktop = () => window.matchMedia('(min-width: 960px)').matches

onMounted(() => {
  const section = document.querySelector('.main-mixed-carousel.desktop-only')
  const header = document.querySelector('.main-header')

  // Only set up header-light observer on desktop
  if (section && header && isDesktop()) {
    section.addEventListener('touchstart', handleTouchStart, { passive: true })
    section.addEventListener('touchend', handleTouchEnd, { passive: true })

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!isDesktop()) {
            header.classList.remove('header-light')
            return
          }
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            header.classList.add('header-light')
          } else {
            header.classList.remove('header-light')
          }
        })
      },
      { threshold: [0, 0.3, 0.5] }
    )
    observer.observe(section)

    resizeHandler = () => {
      if (!isDesktop()) {
        header.classList.remove('header-light')
      }
    }
    window.addEventListener('resize', resizeHandler, { passive: true })
  } else if (header) {
    header.classList.remove('header-light')
  }

  // Start auto-play
  startAutoPlay()

  // Mobile: track visible card with IntersectionObserver
  if (mobileContainer.value) {
    const cards = mobileContainer.value.querySelectorAll('.mixed-carousel-mobile-card')
    mobileObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const idx = parseInt(entry.target.dataset.index, 10)
            if (!isNaN(idx)) mobileActiveIndex.value = idx
          }
        })
      },
      { root: mobileContainer.value, threshold: 0.5 }
    )
    cards.forEach((card) => mobileObserver.observe(card))
  }
})

onBeforeUnmount(() => {
  const section = document.querySelector('.main-mixed-carousel.desktop-only')
  if (section) {
    section.removeEventListener('touchstart', handleTouchStart)
    section.removeEventListener('touchend', handleTouchEnd)
  }
  if (observer) {
    observer.disconnect()
    observer = null
  }
  if (mobileObserver) {
    mobileObserver.disconnect()
    mobileObserver = null
  }
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler)
    resizeHandler = null
  }
  stopAutoPlay()
  if (autoPlayPauseTimer) { clearTimeout(autoPlayPauseTimer); autoPlayPauseTimer = null }
  const header = document.querySelector('.main-header')
  if (header) header.classList.remove('header-light')
})
</script>

<style lang="scss" scoped>
/* ===================== */
/* DESKTOP: Full-screen  */
/* ===================== */
.desktop-only {
  @apply hidden;
  @media (min-width: 960px) {
    @apply block;
  }
}

.main-mixed-carousel {
  @apply relative w-full overflow-hidden;
  height: 100dvh;
  margin-top: -100px;
  border-radius: 0 0 24px 24px;
  -webkit-mask-image: radial-gradient(white, black);
  mask-image: radial-gradient(white, black);

  .mixed-carousel-track {
    @apply flex w-full h-full;
    transition: transform 1.2s cubic-bezier(0.25, 0.1, 0.25, 1);

    .mixed-carousel-slide {
      @apply relative flex-shrink-0;
      width: 100%;
      height: 100%;

      .mixed-carousel-image {
        @apply w-full h-full object-cover absolute inset-0;
      }

      .mixed-carousel-overlay {
        @apply absolute inset-0 pointer-events-none;
        background: rgba(0, 0, 0, 0.3);
      }

      .mixed-carousel-info {
        @apply absolute left-0 z-2 flex items-center gap-6;
        bottom: 80px;
        left: 60px;
        right: 60px;

        .mixed-carousel-breadcrumb {
          @apply flex items-center gap-3 no-underline flex-shrink-0;
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border-radius: 50px;
          padding: 12px 24px;
          color: rgba(255, 255, 255, 0.85);
          font-size: 16px;
          font-weight: 400;
          letter-spacing: 0.02em;
          transition: background 0.3s ease;

          &:hover {
            background: rgba(0, 0, 0, 0.4);
            color: #fff;
          }

          svg {
            flex-shrink: 0;
          }
        }

        .mixed-carousel-excerpt {
          @apply text-white/70 font-FHOscar font-light m-0;
          font-size: 16px;
          line-height: 1.5;
          max-width: 500px;
        }
      }
    }
  }

  .mixed-carousel-nav {
    @apply absolute top-1/2 -translate-y-1/2 z-10 flex items-center justify-center
    text-white cursor-pointer border-0 transition-all duration-300;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(4px);

    &:hover {
      background: rgba(0, 0, 0, 0.5);
    }

    svg {
      width: 28px;
      height: 28px;
    }
  }

  .mixed-carousel-prev {
    left: 40px;
  }

  .mixed-carousel-next {
    right: 40px;
  }

  .mixed-carousel-dots {
    @apply absolute left-1/2 -translate-x-1/2 z-10 flex gap-3;
    bottom: 32px;

    .mixed-carousel-dot {
      @apply cursor-pointer border-0 rounded-full transition-all duration-300;
      width: 12px;
      height: 12px;
      background: rgba(255, 255, 255, 0.4);

      &.active {
        background: rgba(255, 255, 255, 1);
        width: 32px;
        border-radius: 6px;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.7);
      }
    }
  }
}

/* ===================== */
/* MOBILE: Scroll cards  */
/* ===================== */
.mobile-only {
  @apply block;
  @media (min-width: 960px) {
    @apply hidden;
  }
}

.main-mixed-carousel-mobile {
  @apply relative z-1 overflow-hidden px-4;
  padding-top: 100px;
  padding-bottom: 40px;

  .mixed-carousel-mobile-container {
    @apply flex flex-row overflow-x-auto snap-x snap-mandatory gap-4;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }

    .mixed-carousel-mobile-card {
      @apply flex-shrink-0 overflow-hidden relative snap-center rounded-2xl;
      width: calc(100vw - 48px);
      height: 500px;

      .mixed-carousel-mobile-image {
        @apply w-full h-full object-cover absolute inset-0;
      }

      .mixed-carousel-mobile-overlay {
        @apply absolute inset-0 pointer-events-none;
        background: linear-gradient(
          to top,
          rgba(0, 0, 0, 0.6) 0%,
          rgba(0, 0, 0, 0.1) 35%,
          transparent 55%
        );
      }

      .mixed-carousel-mobile-info {
        @apply absolute bottom-0 left-0 right-0 p-4 z-2 flex flex-row flex-wrap items-center gap-2;

        .mixed-carousel-mobile-link {
          @apply flex items-center gap-2 no-underline;
          background: rgba(0, 0, 0, 0.2);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border-radius: 50px;
          padding: 10px 18px;
          color: rgba(255, 255, 255, 0.85);
          font-size: 13px;
          font-weight: 400;
          letter-spacing: 0.02em;

          svg {
            flex-shrink: 0;
          }
        }

        .mixed-carousel-mobile-excerpt {
          @apply text-white/60 font-FHOscar font-light m-0;
          font-size: 13px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      }
    }
  }

  .mixed-carousel-mobile-dots {
    @apply flex justify-center gap-2 mt-4;

    .mixed-carousel-mobile-dot {
      @apply border-0 rounded-full transition-all duration-300 cursor-default;
      width: 8px;
      height: 8px;
      background: rgba(0, 0, 0, 0.15);

      &.active {
        background: rgba(0, 0, 0, 0.5);
        width: 24px;
        border-radius: 4px;
      }
    }
  }
}
</style>
